import { LexicalEditor } from 'lexical';
import { cloneDeep } from 'lodash';
import { updateReferenceCitationsIds } from 'modules/References/utils/updateReferenceCitationsIds';
import * as Models from 'models';
import { createLexicalTextEditor } from '../editors';
import { $getReferenceCitationNodes } from '../Plugins/ReferenceCitationPlugin';
import { $replaceReferenceCitationId } from '../Plugins/ReferenceCitationPlugin/utils/replaceReferenceCitationId';

function updateReferenceCitationsIfChanged(
  component: Models.TextComponent, editor: LexicalEditor
): Models.TextComponent;
function updateReferenceCitationsIfChanged(
  component: Models.TextComponentMap, editor: LexicalEditor
): Models.TextComponentMap;
function updateReferenceCitationsIfChanged(
  component: Models.TextComponentMap | Models.TextComponent,
  editor: LexicalEditor,
): Models.TextComponentMap | Models.TextComponent {
  const updatedReferences = editor.read(
    () => $getReferenceCitationNodes().map(node => node.getReferenceId()),
  );

  return updateReferenceCitationsIds(component, updatedReferences);
}

export function updateReferenceCitationsOnComponent(
  textComponent: Models.TextComponentMap,
): Models.TextComponentMap {
  const serializedState = textComponent.get('lexicalState');
  if (!serializedState) {
    return updateReferenceCitationsIds(textComponent, []);
  }
  const editor = createLexicalTextEditor(serializedState);

  return updateReferenceCitationsIfChanged(textComponent, editor);
}

export function replaceReferenceCitationIdOnComponent(
  textComponent: Models.TextComponentMap,
  referenceId: string,
  newReferenceCitationId: string,
): Models.TextComponentMap {
  const serializedState = textComponent.get('lexicalState');
  if (!serializedState) {
    return updateReferenceCitationsIds(textComponent, []);
  }
  const editor = createLexicalTextEditor(serializedState);
  editor.update(
    () => $replaceReferenceCitationId(referenceId, newReferenceCitationId),
    { discrete: true },
  );
  const updatedComponent = textComponent.set('lexicalState', JSON.stringify(editor.getEditorState()));

  return updateReferenceCitationsIfChanged(updatedComponent, editor);
}

export function replaceReferenceCitationsOnComponent(
  textComponent: Models.TextComponent,
  referenceIdsByOriginal: Record<string, string>,
): Models.TextComponent {
  const { lexicalState } = textComponent;
  if (!lexicalState) {
    return updateReferenceCitationsIds(textComponent, []);
  }

  const editor = createLexicalTextEditor(lexicalState);
  editor.update(() => {
    $getReferenceCitationNodes().forEach((node) => {
      const oldReferenceCitationId = node.getReferenceId();
      const newReferenceCitationId = referenceIdsByOriginal[oldReferenceCitationId];

      if (newReferenceCitationId) {
        node.setReferenceId(newReferenceCitationId);
      }
    });
  }, { discrete: true });

  const updatedComponent = { ...textComponent, lexicalState: JSON.stringify(editor.getEditorState()) };

  return updateReferenceCitationsIfChanged(updatedComponent, editor);
}

export function replaceOrRemoveReferenceCitationsOnComponent(
  textComponent: Models.TextComponent,
  getActualReferenceCitationId: (referenceId: string) => string,
): Models.TextComponent {
  const { lexicalState } = textComponent;
  if (!lexicalState) {
    return updateReferenceCitationsIds(textComponent, []);
  }

  const referenceCitationsInComponent = [...textComponent.referenceCitations];
  const referenceCitations: string[] = [];

  const editor = createLexicalTextEditor(lexicalState);
  editor.update(() => {
    $getReferenceCitationNodes().forEach((node) => {
      const cachedId = referenceCitationsInComponent.shift();
      const currentId = node.getReferenceId();
      if (cachedId !== currentId) {
        // eslint-disable-next-line no-console
        console.error(`ReferenceId mismatch: ${cachedId ?? 'EMPTY'} vs ${currentId}`);
      }

      const newId = getActualReferenceCitationId(currentId);
      if (newId) {
        node.setReferenceId(newId);
        referenceCitations.push(newId);
      } else {
        node.remove();
      }
    });
  }, { discrete: true });

  const updatedComponent = cloneDeep(textComponent);
  updatedComponent.lexicalState = JSON.stringify(editor.getEditorState());
  updatedComponent.referenceCitations = referenceCitations;

  return updatedComponent;
}

export function removeReferenceCitationIdOnComponent(
  textComponent: Models.TextComponentMap,
  referenceId: string,
): Models.TextComponentMap {
  const serializedState = textComponent.get('lexicalState');
  if (!serializedState) {
    return updateReferenceCitationsIds(textComponent, []);
  }

  const editor = createLexicalTextEditor(serializedState);
  editor.update(() => {
    $getReferenceCitationNodes().forEach((node) => {
      if (node.getReferenceId() === referenceId) {
        node.remove();
      }
    });
  }, { discrete: true });

  const updatedComponent = textComponent.set('lexicalState', JSON.stringify(editor.getEditorState()));

  return updateReferenceCitationsIfChanged(updatedComponent, editor);
}
